import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { MyDomain } from "../../App";
import { useParams, useHistory } from "react-router-dom";
import { useAppContext } from "../../AppContext";
import MPHeader1 from "../MainPageComps/MPHeader1";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useTranslation } from "react-i18next";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

function MapUpdater({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (
      center &&
      center[0] &&
      center[1] &&
      !isNaN(center[0]) &&
      !isNaN(center[1])
    ) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);
  return null;
}

const PlaceholderImage = ({ type = "main", alt, windowWidth }) => {
  switch (type) {
    case "main":
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            backgroundColor: "#f5f5f5",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
          }}
        >
          <div style={{ fontSize: "48px" }}>✂️</div>
          <div style={{ fontSize: "14px", color: "#999", fontWeight: "500" }}>
            No Image
          </div>
        </div>
      );
    case "secondary":
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            backgroundColor: "#ececec",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ fontSize: "32px" }}>📷</div>
        </div>
      );
    case "profile":
      return (
        <div
          style={{
            width: windowWidth <= 768 ? "60px" : "70px",
            height: windowWidth <= 768 ? "60px" : "70px",
            borderRadius: "50%",
            backgroundColor: "#007AFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "3px solid #007AFF",
          }}
        >
          <div style={{ fontSize: "32px" }}>👤</div>
        </div>
      );
    default:
      return null;
  }
};

export default function AllCategories({ CategoryName }) {
  const { t } = useTranslation();
  const params = useParams();
  const history = useHistory();
  const category = CategoryName || params.CategoryName;

  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState({});
  const [barberImages, setBarberImages] = useState({});
  const [barberRatings, setBarberRatings] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [mapCenter, setMapCenter] = useState([62.0, 15.0]);
  const [mapZoom, setMapZoom] = useState(5);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [searchTerm, setSearchTerm] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [userLocation, setUserLocation] = useState(null);
  const [showNearbyOnly, setShowNearbyOnly] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);

  const itemsPerPage = 10;
  const listContainerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (category === "haircut") fetchBarbers();
  }, [category]);

  useEffect(() => {
    if (barbers.length > 0) {
      const validBarbers = barbers.filter(
        (item) =>
          item.location?.lat &&
          item.location?.long &&
          !isNaN(parseFloat(item.location.lat)) &&
          !isNaN(parseFloat(item.location.long)),
      );
      if (validBarbers.length > 0) {
        const centerLat =
          validBarbers.reduce(
            (sum, item) => sum + parseFloat(item.location.lat),
            0,
          ) / validBarbers.length;
        const centerLng =
          validBarbers.reduce(
            (sum, item) => sum + parseFloat(item.location.long),
            0,
          ) / validBarbers.length;
        if (!isNaN(centerLat) && !isNaN(centerLng))
          setMapCenter([centerLat, centerLng]);
      }
    }
  }, [barbers]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, ratingFilter, showNearbyOnly]);

  const fetchBarbers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${MyDomain}Barbers2/GetBarbersse`);
      setBarbers(response.data);
      response.data.forEach((item) => {
        if (item.location?.lat && item.location?.long)
          getAddressFromCoordinates(
            item.barber.id,
            item.location.lat,
            item.location.long,
          );
        fetchBarberImages(item.barber.id);
        fetchBarberReviews(item.barber.phoneNumber);
      });
    } catch (error) {
      console.error("Error fetching barbers:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBarberImages = async (barberId) => {
    try {
      const response = await axios.get(
        `${MyDomain}Barbers2/GetAllImagesByBarberIdse/${barberId}`,
      );
      setBarberImages((prev) => ({
        ...prev,
        [barberId]: {
          mainImage: response.data.mainImage,
          mainImage2: response.data.mainImage2,
          mainImage3: response.data.mainImage3,
        },
      }));
    } catch (error) {
      console.error(`Error fetching images for barber ${barberId}:`, error);
    }
  };

  const fetchBarberReviews = async (phoneNumber) => {
    try {
      const response = await axios.post(
        `${MyDomain}Barbers2/GetAllBarberDatase`,
        { phoneNumber },
      );
      const reviews = response.data.allReviews || [];
      if (reviews.length > 0) {
        const average = (
          reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length
        ).toFixed(1);
        setBarberRatings((prev) => ({
          ...prev,
          [phoneNumber]: { average, count: reviews.length },
        }));
      }
    } catch (error) {
      console.error(`Error fetching reviews for ${phoneNumber}:`, error);
    }
  };

  const getAddressFromCoordinates = async (barberId, lat, lng) => {
    const apiKey = "AIzaSyA4HcCM2bP0v8Chi2bdFL7vAhCVkepKsBY";
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`,
      );
      const data = await response.json();
      if (data.results[0]) {
        const comps = data.results[0].address_components;
        const street =
          comps.find((c) => c.types.includes("route"))?.long_name || "";
        const city =
          comps.find((c) => c.types.includes("locality"))?.long_name || "";
        const address =
          street && city
            ? `${street}, ${city}`
            : data.results[0].formatted_address;
        setAddresses((prev) => ({ ...prev, [barberId]: address }));
      }
    } catch (error) {
      console.error("Error fetching address:", error);
    }
  };

  const renderStars = (rating) => {
    const starSize = windowWidth <= 768 ? 12 : 14;
    return (
      <div style={{ display: "flex", gap: "1px" }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            width={starSize}
            height={starSize}
            viewBox="0 0 24 24"
            fill={star <= rating ? "#FCD34D" : "#E5E7EB"}
            stroke={star <= rating ? "#F59E0B" : "#D1D5DB"}
            strokeWidth="1"
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        ))}
      </div>
    );
  };

  const handleBarberClick = (phoneNumber) => {
    history.push(`/barber-main-page/${phoneNumber}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBarberHover = (barberId, lat, lng) => {
    const latitude = parseFloat(lat);
    const longitude = parseFloat(lng);
    if (!isNaN(latitude) && !isNaN(longitude)) {
      setSelectedBarber(barberId);
      setMapCenter([latitude, longitude]);
      setMapZoom(12);
    }
  };

  const handleBarberLeave = () => {
    setSelectedBarber(null);
    setMapZoom(8);
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const handleGetUserLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setShowNearbyOnly(true);
        setMapCenter([position.coords.latitude, position.coords.longitude]);
        setMapZoom(13);
        setLocationLoading(false);
      },
      (error) => {
        const msgs = {
          [error.PERMISSION_DENIED]:
            "Please allow location access in your browser settings.",
          [error.POSITION_UNAVAILABLE]: "Location information is unavailable.",
          [error.TIMEOUT]: "Location request timed out.",
        };
        alert(
          "Unable to get your location. " +
            (msgs[error.code] || "An unknown error occurred."),
        );
        setLocationLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  const handleClearLocation = () => {
    setUserLocation(null);
    setShowNearbyOnly(false);
    const validBarbers = barbers.filter(
      (item) =>
        item.location?.lat &&
        item.location?.long &&
        !isNaN(parseFloat(item.location.lat)) &&
        !isNaN(parseFloat(item.location.long)),
    );
    if (validBarbers.length > 0) {
      const centerLat =
        validBarbers.reduce(
          (sum, item) => sum + parseFloat(item.location.lat),
          0,
        ) / validBarbers.length;
      const centerLng =
        validBarbers.reduce(
          (sum, item) => sum + parseFloat(item.location.long),
          0,
        ) / validBarbers.length;
      if (!isNaN(centerLat) && !isNaN(centerLng)) {
        setMapCenter([centerLat, centerLng]);
        setMapZoom(12);
      }
    }
  };

  const handleClearAllFilters = () => {
    setSearchTerm("");
    setRatingFilter("all");
    if (showNearbyOnly) handleClearLocation();
  };

  const getFilteredBarbers = () => {
    let filtered = barbers;
    if (searchTerm.trim()) {
      filtered = filtered.filter((item) => {
        const name = item.barber.name?.toLowerCase() || "";
        const phone = item.barber.phoneNumber?.toLowerCase() || "";
        return (
          name.includes(searchTerm.toLowerCase()) ||
          phone.includes(searchTerm.toLowerCase())
        );
      });
    }
    if (ratingFilter !== "all") {
      filtered = filtered.filter((item) => {
        const rating = barberRatings[item.barber.phoneNumber];
        if (!rating) return false;
        const avg = parseFloat(rating.average);
        if (ratingFilter === "5") return avg >= 4.5;
        if (ratingFilter === "4") return avg >= 4 && avg < 4.5;
        if (ratingFilter === "3") return avg >= 3 && avg < 4;
        if (ratingFilter === "below3") return avg < 3;
        return true;
      });
    }
    if (showNearbyOnly && userLocation) {
      filtered = filtered.filter((item) => {
        if (!item.location?.lat || !item.location?.long) return false;
        return (
          calculateDistance(
            userLocation.lat,
            userLocation.lng,
            parseFloat(item.location.lat),
            parseFloat(item.location.long),
          ) <= 15
        );
      });
    }
    return filtered;
  };

  const filteredBarbers = getFilteredBarbers();
  const totalPages = Math.ceil(filteredBarbers.length / itemsPerPage);
  const currentBarbers = filteredBarbers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const validBarbersForMap = filteredBarbers.filter(
    (item) =>
      item.location?.lat &&
      item.location?.long &&
      !isNaN(parseFloat(item.location.lat)) &&
      !isNaN(parseFloat(item.location.long)),
  );
  const showMap = windowWidth > 1024;
  const isMobile = windowWidth <= 768;
  const hasActiveFilters =
    searchTerm || ratingFilter !== "all" || showNearbyOnly;

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    if (isMobile) window.scrollTo({ top: 0, behavior: "smooth" });
    else if (listContainerRef.current)
      listContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ─── Shared chip style ───────────────────────────────────────────────
  const chipBase = {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    height: "34px",
    padding: "0 13px",
    borderRadius: "999px",
    border: "1px solid #e0e0e0",
    backgroundColor: "white",
    fontSize: "13px",
    color: "#444",
    whiteSpace: "nowrap",
    cursor: "pointer",
    flexShrink: 0,
    fontFamily: "inherit",
    transition: "all 0.15s",
  };

  const chipActive = {
    ...chipBase,
    backgroundColor: "#18181b",
    color: "white",
    border: "1px solid #18181b",
  };

  const chipLocationActive = {
    ...chipBase,
    backgroundColor: "#EBF5FF",
    color: "#1a56db",
    border: "1px solid #93c5fd",
  };

  const selectChip = {
    ...chipBase,
    paddingRight: "28px",
    appearance: "none",
    WebkitAppearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 9px center",
    outline: "none",
    cursor: "pointer",
  };

  return (
    <div
      style={{
        paddingLeft:
          windowWidth <= 1024
            ? "0%"
            : windowWidth <= 1920
              ? "10%"
              : windowWidth <= 2560
                ? "20%"
                : windowWidth <= 3840
                  ? "25%"
                  : "30%",
        paddingRight:
          windowWidth <= 1024
            ? "0%"
            : windowWidth <= 1920
              ? "10%"
              : windowWidth <= 2560
                ? "20%"
                : windowWidth <= 3840
                  ? "25%"
                  : "30%",
        fontFamily: "Droid Arabic Kufi, sans-serif",
      }}
    >
      {/* ─── Mobile / Tablet sticky header ─────────────────────────────── */}
      {windowWidth <= 1024 && (
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 1000,
            backgroundColor: "white",
            borderBottom: "1px solid #e8e8e8",
            padding: "10px 16px 0",
          }}
        >
          {/* Title row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <button
              onClick={() => history.goBack()}
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                border: "none",
                backgroundColor: "#f2f2f2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#333"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>

            <h2
              style={{
                flex: 1,
                textAlign: "center",
                margin: 0,
                fontSize: "16px",
                fontWeight: "600",
                color: "#111",
                textTransform: "capitalize",
              }}
            >
              {category} Salons
            </h2>

            <div style={{ width: 36 }} />
          </div>

          {/* Search bar */}
          <div style={{ position: "relative", marginBottom: "10px" }}>
            <svg
              style={{
                position: "absolute",
                left: 11,
                top: "50%",
                transform: "translateY(-50%)",
                width: 15,
                height: 15,
                color: "#aaa",
                pointerEvents: "none",
              }}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder={t("SearchByNameOrPhoneNumber")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                height: "38px",
                padding: "0 36px 0 34px",
                fontSize: "14px",
                border: "1px solid #e0e0e0",
                borderRadius: "999px",
                backgroundColor: "#f7f7f7",
                color: "#111",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 18,
                  height: 18,
                  border: "none",
                  borderRadius: "50%",
                  backgroundColor: "#bbb",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontSize: 11,
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Filter chips row — horizontally scrollable */}
          <div
            style={{
              display: "flex",
              gap: "7px",
              overflowX: "auto",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              paddingBottom: "10px",
            }}
          >
            {/* Rating dropdown chip */}
            <select
              value={ratingFilter}
              onChange={(e) => setRatingFilter(e.target.value)}
              style={{
                ...selectChip,
                ...(ratingFilter !== "all"
                  ? {
                      backgroundColor: "#18181b",
                      color: "white",
                      border: "1px solid #18181b",
                    }
                  : {}),
              }}
            >
              <option value="all">{t("AllRatings")}</option>
              <option value="5">★ 4.5 – 5.0</option>
              <option value="4">★ 4.0 – 4.4</option>
              <option value="3">★ 3.0 – 3.9</option>
              <option value="below3">★ {t("Below")} 3.0</option>
            </select>

            {/* Near me chip */}
            <button
              onClick={
                showNearbyOnly ? handleClearLocation : handleGetUserLocation
              }
              disabled={locationLoading}
              style={showNearbyOnly ? chipLocationActive : chipBase}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              {locationLoading
                ? t("GettingLocation")
                : showNearbyOnly
                  ? `${t("CloseToMe")} ✓`
                  : t("CloseToMe")}
            </button>

            {/* Clear all chip — only shown when any filter is active */}
            {hasActiveFilters && (
              <button
                onClick={handleClearAllFilters}
                style={{
                  ...chipBase,
                  color: "#888",
                  border: "1px solid #e0e0e0",
                }}
              >
                ✕ Clear
              </button>
            )}
          </div>
        </div>
      )}

      {windowWidth > 1024 && <MPHeader1 />}

      <div style={{ padding: isMobile ? "10px" : "20px" }}>
        {category === "haircut" && (
          <div>
            {loading ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "60px 20px",
                  minHeight: "400px",
                }}
              >
                <div
                  style={{
                    width: "52px",
                    height: "52px",
                    border: "3px solid #f0f0f0",
                    borderTop: "3px solid #007AFF",
                    borderRadius: "50%",
                    animation: "spin 0.9s linear infinite",
                    marginBottom: "20px",
                  }}
                />
                <h3
                  style={{
                    margin: "0 0 6px",
                    fontSize: "17px",
                    fontWeight: "600",
                    color: "#333",
                  }}
                >
                  Loading Salons
                </h3>
                <p style={{ margin: 0, fontSize: "13px", color: "#999" }}>
                  Finding the best barbers for you…
                </p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            ) : (
              <>
                {/* ─── Desktop search & filter bar ─────────────────────────── */}
                {windowWidth > 1024 && (
                  <div
                    style={{
                      maxWidth: showMap ? "48%" : "100%",
                      marginBottom: "20px",
                      display: "flex",
                      gap: "10px",
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    {/* Search */}
                    <div style={{ flex: "1 1 280px", position: "relative" }}>
                      <input
                        type="text"
                        placeholder={t("SearchByNameOrPhoneNumber")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "10px 40px 10px 14px",
                          fontSize: "14px",
                          border: "1px solid #e0e0e0",
                          borderRadius: "8px",
                          outline: "none",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#007AFF")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
                      />
                      <span
                        style={{
                          position: "absolute",
                          right: 12,
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: 16,
                          color: "#aaa",
                        }}
                      >
                        🔍
                      </span>
                    </div>

                    <select
                      value={ratingFilter}
                      onChange={(e) => setRatingFilter(e.target.value)}
                      style={{
                        padding: "10px 14px",
                        fontSize: "14px",
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        outline: "none",
                        cursor: "pointer",
                        backgroundColor: "white",
                      }}
                    >
                      <option value="all">{t("AllRatings")}</option>
                      <option value="5">⭐ 4.5 – 5.0</option>
                      <option value="4">⭐ 4.0 – 4.4</option>
                      <option value="3">⭐ 3.0 – 3.9</option>
                      <option value="below3">⭐ {t("Below")} 3.0</option>
                    </select>

                    <button
                      onClick={
                        showNearbyOnly
                          ? handleClearLocation
                          : handleGetUserLocation
                      }
                      disabled={locationLoading}
                      style={{
                        padding: "10px 18px",
                        fontSize: "14px",
                        backgroundColor: showNearbyOnly ? "#1a56db" : "#18181b",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "7px",
                      }}
                    >
                      📍{" "}
                      {locationLoading
                        ? t("GettingLocation")
                        : showNearbyOnly
                          ? `Within 15km ✓`
                          : t("CloseToMe")}
                    </button>

                    {hasActiveFilters && (
                      <button
                        onClick={handleClearAllFilters}
                        style={{
                          padding: "10px 16px",
                          fontSize: "14px",
                          backgroundColor: "#f5f5f5",
                          color: "#666",
                          border: "none",
                          borderRadius: "8px",
                          cursor: "pointer",
                        }}
                      >
                        Clear filters
                      </button>
                    )}

                    <p
                      style={{
                        width: "100%",
                        margin: 0,
                        fontSize: 12,
                        color: "#888",
                      }}
                    >
                      {t("ShowingMoreThan")} {filteredBarbers.length - 1}{" "}
                      {t("SalonsFor")}{" "}
                      <span style={{ textTransform: "capitalize" }}>
                        {category}
                      </span>
                    </p>
                  </div>
                )}

                {/* Results count for mobile */}
                {isMobile && (
                  <p
                    style={{
                      margin: "0 0 8px",
                      padding: "0 4px",
                      fontSize: 12,
                      color: "#888",
                    }}
                  >
                    {filteredBarbers.length} {t("SalonsFor")}{" "}
                    <span style={{ textTransform: "capitalize" }}>
                      {category}
                    </span>
                  </p>
                )}

                {/* ─── Split-screen layout ──────────────────────────────────── */}
                <div style={{ display: "flex", gap: "20px" }}>
                  {/* List */}
                  <div
                    style={{
                      flex: showMap ? "0 0 48%" : "1",
                      display: "flex",
                      flexDirection: "column",
                      maxHeight: isMobile ? "none" : "calc(100vh - 280px)",
                    }}
                  >
                    <div
                      ref={listContainerRef}
                      style={{
                        flex: 1,
                        overflowY: isMobile ? "visible" : "auto",
                        paddingRight: isMobile ? 0 : "10px",
                      }}
                    >
                      {currentBarbers.length === 0 ? (
                        <div
                          style={{
                            textAlign: "center",
                            padding: "60px 20px",
                            backgroundColor: "#f9f9f9",
                            borderRadius: "12px",
                          }}
                        >
                          <p style={{ fontSize: "18px", color: "#666" }}>
                            No barbers found matching your criteria
                          </p>
                          <p
                            style={{
                              fontSize: "14px",
                              color: "#999",
                              marginTop: "10px",
                            }}
                          >
                            Try adjusting your search or filters
                          </p>
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: isMobile ? "12px" : "16px",
                          }}
                        >
                          {currentBarbers.map((item, index) => {
                            const images = barberImages[item.barber.id] || {};
                            const hasMainImg =
                              images.mainImage?.image || item.barber.image;
                            const hasImg2 = images.mainImage2?.image;
                            const hasImg3 = images.mainImage3?.image;
                            const rating =
                              barberRatings[item.barber.phoneNumber];

                            return (
                              <div
                                key={item.barber.id || index}
                                onMouseEnter={() =>
                                  showMap &&
                                  item.location?.lat &&
                                  handleBarberHover(
                                    item.barber.id,
                                    item.location.lat,
                                    item.location.long,
                                  )
                                }
                                onMouseLeave={() =>
                                  showMap && handleBarberLeave()
                                }
                                onClick={() =>
                                  handleBarberClick(item.barber.phoneNumber)
                                }
                                style={{
                                  backgroundColor: "white",
                                  borderRadius: "10px",
                                  overflow: "hidden",
                                  boxShadow:
                                    selectedBarber === item.barber.id
                                      ? "0 6px 18px rgba(0,0,0,0.18)"
                                      : "0 1px 4px rgba(0,0,0,0.08)",
                                  border:
                                    selectedBarber === item.barber.id
                                      ? "2px solid #111"
                                      : "1px solid #ebebeb",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  display: "flex",
                                  flexDirection: "row", // always horizontal on mobile now
                                  height: isMobile ? "110px" : "220px",
                                }}
                              >
                                {/* Image grid */}
                                <div
                                  style={{
                                    flexShrink: 0,
                                    width: isMobile ? "110px" : "45%",
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gridTemplateRows: "60% 40%",
                                    gap: "2px",
                                    backgroundColor: "#f0f0f0",
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      gridColumn: "1 / 3",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {hasMainImg ? (
                                      <img
                                        src={hasMainImg}
                                        alt={item.barber.name}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    ) : (
                                      <PlaceholderImage
                                        type="main"
                                        windowWidth={windowWidth}
                                      />
                                    )}
                                  </div>
                                  {!isMobile && (
                                    <>
                                      <div style={{ overflow: "hidden" }}>
                                        {hasImg2 ? (
                                          <img
                                            src={hasImg2}
                                            alt=""
                                            style={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "cover",
                                            }}
                                          />
                                        ) : (
                                          <PlaceholderImage
                                            type="secondary"
                                            windowWidth={windowWidth}
                                          />
                                        )}
                                      </div>
                                      <div style={{ overflow: "hidden" }}>
                                        {hasImg3 ? (
                                          <img
                                            src={hasImg3}
                                            alt=""
                                            style={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "cover",
                                            }}
                                          />
                                        ) : (
                                          <PlaceholderImage
                                            type="secondary"
                                            windowWidth={windowWidth}
                                          />
                                        )}
                                      </div>
                                    </>
                                  )}
                                </div>

                                {/* Info */}
                                <div
                                  style={{
                                    flex: 1,
                                    padding: isMobile
                                      ? "10px 12px"
                                      : "18px 20px",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    minWidth: 0,
                                    overflow: "hidden",
                                  }}
                                >
                                  <div>
                                    {/* Name + avatar row */}
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "9px",
                                        marginBottom: isMobile ? "4px" : "10px",
                                      }}
                                    >
                                      {item.barber.image ? (
                                        <img
                                          src={item.barber.image}
                                          alt={item.barber.name}
                                          style={{
                                            width: isMobile ? "34px" : "46px",
                                            height: isMobile ? "34px" : "46px",
                                            borderRadius: "50%",
                                            objectFit: "cover",
                                            border: "2px solid #007AFF",
                                            flexShrink: 0,
                                          }}
                                        />
                                      ) : (
                                        <div
                                          style={{
                                            width: isMobile ? "34px" : "46px",
                                            height: isMobile ? "34px" : "46px",
                                            borderRadius: "50%",
                                            backgroundColor: "#007AFF",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: isMobile
                                                ? "16px"
                                                : "22px",
                                            }}
                                          >
                                            👤
                                          </span>
                                        </div>
                                      )}
                                      <div style={{ minWidth: 0 }}>
                                        <h3
                                          style={{
                                            margin: 0,
                                            fontSize: isMobile
                                              ? "14px"
                                              : "17px",
                                            fontWeight: "600",
                                            color: "#111",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                          }}
                                        >
                                          {item.barber.name}
                                        </h3>
                                        {!isMobile && (
                                          <p
                                            style={{
                                              margin: 0,
                                              fontSize: "12px",
                                              color: "#aaa",
                                            }}
                                          >
                                            📱 {item.barber.phoneNumber}
                                          </p>
                                        )}
                                      </div>
                                    </div>

                                    {/* Address */}
                                    {addresses[item.barber.id] && (
                                      <p
                                        style={{
                                          margin: 0,
                                          fontSize: isMobile ? "11px" : "13px",
                                          color: "#777",
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                      >
                                        📍 {addresses[item.barber.id]}
                                      </p>
                                    )}
                                  </div>

                                  {/* Rating */}
                                  {rating && (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px",
                                        marginTop: isMobile ? "6px" : "0",
                                      }}
                                    >
                                      {renderStars(
                                        Math.round(parseFloat(rating.average)),
                                      )}
                                      <span
                                        style={{
                                          fontSize: isMobile ? "11px" : "12px",
                                          fontWeight: "600",
                                          color: "#333",
                                        }}
                                      >
                                        {rating.average}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: isMobile ? "10px" : "11px",
                                          color: "#aaa",
                                        }}
                                      >
                                        ({rating.count})
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "6px",
                          padding: "12px 8px",
                          backgroundColor: "white",
                          borderTop: "1px solid #ebebeb",
                          position: "sticky",
                          bottom: 0,
                          zIndex: 10,
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          onClick={() =>
                            currentPage > 1 && handlePageChange(currentPage - 1)
                          }
                          disabled={currentPage === 1}
                          style={{
                            padding: "7px 14px",
                            backgroundColor:
                              currentPage === 1 ? "#eee" : "#18181b",
                            color: currentPage === 1 ? "#bbb" : "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor:
                              currentPage === 1 ? "not-allowed" : "pointer",
                            fontSize: "12px",
                          }}
                        >
                          ← Prev
                        </button>

                        <div
                          style={{
                            display: "flex",
                            gap: "4px",
                            flexWrap: "wrap",
                            justifyContent: "center",
                          }}
                        >
                          {Array.from(
                            { length: Math.min(totalPages, isMobile ? 5 : 10) },
                            (_, i) => {
                              const maxPages = isMobile ? 5 : 10;
                              let pageNum;
                              if (totalPages <= maxPages) pageNum = i + 1;
                              else if (currentPage <= Math.floor(maxPages / 2))
                                pageNum = i + 1;
                              else if (
                                currentPage >=
                                totalPages - Math.floor(maxPages / 2)
                              )
                                pageNum = totalPages - maxPages + i + 1;
                              else
                                pageNum =
                                  currentPage - Math.floor(maxPages / 2) + i;
                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => handlePageChange(pageNum)}
                                  style={{
                                    padding: "7px 11px",
                                    backgroundColor:
                                      currentPage === pageNum
                                        ? "#18181b"
                                        : "white",
                                    color:
                                      currentPage === pageNum
                                        ? "white"
                                        : "#444",
                                    border: "1px solid #ddd",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                  }}
                                >
                                  {pageNum}
                                </button>
                              );
                            },
                          )}
                        </div>

                        <button
                          onClick={() =>
                            currentPage < totalPages &&
                            handlePageChange(currentPage + 1)
                          }
                          disabled={currentPage === totalPages}
                          style={{
                            padding: "7px 14px",
                            backgroundColor:
                              currentPage === totalPages ? "#eee" : "#18181b",
                            color:
                              currentPage === totalPages ? "#bbb" : "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor:
                              currentPage === totalPages
                                ? "not-allowed"
                                : "pointer",
                            fontSize: "12px",
                          }}
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Map */}
                  {showMap && (
                    <div
                      style={{
                        flex: "0 0 50%",
                        position: "sticky",
                        top: "20px",
                        height: "calc(100vh - 300px)",
                      }}
                    >
                      <MapContainer
                        center={mapCenter}
                        zoom={mapZoom}
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "12px",
                          overflow: "hidden",
                          boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
                        }}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <MapUpdater center={mapCenter} zoom={mapZoom} />
                        {userLocation && showNearbyOnly && (
                          <Marker
                            position={[userLocation.lat, userLocation.lng]}
                            icon={L.divIcon({
                              className: "custom-user-marker",
                              html: `<div style="background:#007AFF;width:20px;height:20px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div>`,
                              iconSize: [20, 20],
                              iconAnchor: [10, 10],
                            })}
                          >
                            <Popup>
                              <div style={{ textAlign: "center" }}>
                                <strong>📍 Your Location</strong>
                                <p
                                  style={{
                                    margin: "5px 0 0",
                                    fontSize: "12px",
                                  }}
                                >
                                  Showing barbers within 15km
                                </p>
                              </div>
                            </Popup>
                          </Marker>
                        )}
                        {validBarbersForMap.map((item, index) => (
                          <Marker
                            key={item.barber.id || index}
                            position={[
                              parseFloat(item.location.lat),
                              parseFloat(item.location.long),
                            ]}
                          >
                            <Popup>
                              <div style={{ textAlign: "center" }}>
                                <strong>{item.barber.name}</strong>
                                {addresses[item.barber.id] && (
                                  <p
                                    style={{
                                      margin: "5px 0 0",
                                      fontSize: "12px",
                                    }}
                                  >
                                    📍 {addresses[item.barber.id]}
                                  </p>
                                )}
                              </div>
                            </Popup>
                          </Marker>
                        ))}
                      </MapContainer>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
